import { useWSQuery, WSQueryConfig, WSQueryClient } from "@ws-react-query";
import { TaxEstimate, TaxEstimateRequest } from "@wingspanhq/wingspan-api-sdk";
import { taxEstimatesApi } from "@api";
import { WSServiceError } from "../../utils/serviceHelper";

export const QUERY_V3_TAX_ESTIMATE = "QUERY_V3_TAX_ESTIMATE";

const getTaxEstimateQueryKey = (year: number) => [QUERY_V3_TAX_ESTIMATE, year];

const createTaxEstimate = (year: number) => {
  const taxEstimateRequest: TaxEstimateRequest = { year };

  return taxEstimatesApi.createTaxEstimate({ taxEstimateRequest });
};

/**
 * The tax estimate for a year. `createTaxEstimate` is a POST that computes and
 * returns the estimate without persisting anything, so it is consumed as a read
 * (hand-written rather than off the `queries*` registry, which routes every
 * non-`get`/`list`/`search` method to `useMutation`).
 *
 * TODO(v3-blocker): SDK 3.55.3 narrowed `TaxEstimateRequest` to `{ year }`, so
 * filing status and state of residence can no longer be passed in — the
 * generated serializer drops every other key. The estimate is therefore
 * account-wide for the year and does not vary with the answers the tax-rate
 * form collects. `TaxEstimate` still *returns* `filingStatus` and `state`,
 * which suggests the request narrowing is an SDK generation gap rather than an
 * intentional API change; confirm with the backend team before relying on it.
 *
 * `retry: false` because a member with no tax profile yet gets a deterministic
 * failure — retrying only delays the caller's fallback.
 */
export const useQueryTaxEstimate = (
  year: number,
  config?: WSQueryConfig<TaxEstimate, WSServiceError>
) =>
  useWSQuery<TaxEstimate, WSServiceError>(
    getTaxEstimateQueryKey(year),
    () => createTaxEstimate(year),
    { retry: false, ...config }
  );

/**
 * Cache-first read of the same estimate, for a submit handler that needs the
 * computed rate rather than a rendered value. Shares the hook's query key, so
 * the rate the form was displaying is a cache hit rather than a second POST.
 */
export const fetchTaxEstimate = (year: number) =>
  WSQueryClient.fetchQuery<TaxEstimate>({
    queryKey: getTaxEstimateQueryKey(year),
    queryFn: () => createTaxEstimate(year),
    retry: false
  });
